import React from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { METRIC_GROUPS, getAllMetrics } from './analysisMetrics';
import { applyPreset } from './analysisDisplayPrefs';

const AnalysisDisplayControls = ({
  open,
  onToggleOpen,
  visibleMetricIds,
  onVisibleMetricIdsChange,
  showDynamicAssets,
  showDynamicRevenues,
  onShowDynamicAssetsChange,
  onShowDynamicRevenuesChange,
  bundle,
  activeChartTab,
  onActiveChartTabChange,
}) => {
  const staticMetrics = getAllMetrics(bundle).filter((m) => !m.dynamic);
  const assetCount = bundle?.assetNames?.length || 0;
  const revenueCount = bundle?.revenueNames?.length || 0;

  const toggleMetric = (id) => {
    const set = new Set(visibleMetricIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onVisibleMetricIdsChange(Array.from(set));
  };

  const chartTabs = [
    { id: 'netWorth', label: 'Valeur nette' },
    { id: 'debtVsAssets', label: 'Actifs vs dettes' },
    { id: 'cashflow', label: 'Cashflow' },
    { id: 'assetsStack', label: 'Actifs (détail)' },
    { id: 'byAssetType', label: 'Par type d\'actif' },
    { id: 'revenuesStack', label: 'Revenus (détail)' },
  ];

  const grouped = Object.keys(METRIC_GROUPS)
    .map((groupKey) => ({
      key: groupKey,
      label: METRIC_GROUPS[groupKey],
      metrics: staticMetrics.filter((m) => m.group === groupKey),
    }))
    .filter((g) => g.metrics.length > 0);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="analysis-display-controls">
      <button
        type="button"
        onClick={onToggleOpen}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        <span className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="SlidersHorizontal" size={16} className="text-primary" />
          Données affichées
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-muted-foreground" />
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-border space-y-4">
          <div className="flex flex-wrap gap-2 pt-3">
            <Button size="sm" variant="outline" onClick={() => onVisibleMetricIdsChange(applyPreset('essentiel'))}>
              Essentiel
            </Button>
            <Button size="sm" variant="outline" onClick={() => onVisibleMetricIdsChange(applyPreset('complet'))}>
              Complet
            </Button>
            <Button size="sm" variant="outline" onClick={() => onVisibleMetricIdsChange(applyPreset('fiscal'))}>
              Fiscal
            </Button>
            <Button size="sm" variant="outline" onClick={() => onVisibleMetricIdsChange(applyPreset('assuranceCollateral'))}>
              Assurance &amp; collatéral
            </Button>
            <Button size="sm" variant="outline" onClick={() => onVisibleMetricIdsChange(applyPreset('corporatifActionnaires'))}>
              Corporatif &amp; actionnaires
            </Button>
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Graphique principal</p>
            <div className="flex flex-wrap gap-2">
              {chartTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onActiveChartTabChange(tab.id)}
                  className={`rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
                    activeChartTab === tab.id
                      ? 'border-primary bg-primary/10 text-foreground font-medium'
                      : 'border-border text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {grouped.map((group) => (
            <div key={group.key}>
              <p className="text-xs font-semibold text-foreground mb-2">{group.label}</p>
              <div className="flex flex-wrap gap-2">
                {group.metrics.map((m) => (
                  <label
                    key={m.id}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs cursor-pointer transition-colors ${
                      visibleMetricIds.includes(m.id)
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border text-muted-foreground hover:border-primary/40'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={visibleMetricIds.includes(m.id)}
                      onChange={() => toggleMetric(m.id)}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
          ))}

          {(assetCount > 0 || revenueCount > 0) && (
            <div className="pt-2 border-t border-border space-y-2">
              <p className="text-xs font-semibold text-foreground">Séries détaillées</p>
              {assetCount > 0 && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDynamicAssets}
                    onChange={(e) => onShowDynamicAssetsChange(e.target.checked)}
                    className="rounded border-input"
                  />
                  Actifs individuels ({assetCount})
                </label>
              )}
              {revenueCount > 0 && (
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDynamicRevenues}
                    onChange={(e) => onShowDynamicRevenuesChange(e.target.checked)}
                    className="rounded border-input"
                  />
                  Revenus individuels ({revenueCount})
                </label>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalysisDisplayControls;



